import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { saveSupportMessage } from '../utils/loanService';
import Navbar from '../components/Navbar';

const TOPICS = [
  { id:'disbursement', icon:'💰', name:'Loan Disbursement', desc:'Payment not received' },
  { id:'repayment',    icon:'📅', name:'Repayment',         desc:'How to repay my loan' },
  { id:'application',  icon:'📋', name:'Application',       desc:'Help with applying' },
  { id:'account',      icon:'👤', name:'My Account',        desc:'Login or profile issues' },
  { id:'fees',         icon:'🏦', name:'Fees & Charges',    desc:'Questions about fees' },
  { id:'other',        icon:'💬', name:'Other',             desc:'Any other question' },
];

export default function Support() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [topic, setTopic] = useState('');
  const [form, setForm] = useState({ name:'', email:'', message:'' });
  const [errs, setErrs] = useState({});
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const upd = f => e => setForm(p => ({ ...p, [f]: e.target.value }));

  // Pre-fill from logged in user if available
  const displayName = currentUser?.displayName || form.name;
  const displayEmail = currentUser?.email || form.email;

  function validate() {
    const e = {};
    if (!topic) e.topic = true;
    if (!form.name.trim() && !currentUser?.displayName) e.name = true;
    if (!form.email.trim() && !currentUser?.email) e.email = true;
    if (!form.message.trim() || form.message.trim().length < 10) e.message = true;
    return e;
  }

  async function handleSubmit() {
    const e2 = validate(); setErrs(e2);
    if (Object.keys(e2).length) return;
    setLoading(true);
    await saveSupportMessage({
      uid:       currentUser?.uid || 'guest',
      userName:  currentUser?.displayName || form.name,
      userEmail: currentUser?.email || form.email,
      subject:   topic,
      message:   form.message,
      category:  topic,
    });
    setLoading(false);
    setSent(true);
  }

  if (sent) return (
    <>
      <Navbar />
      <div className="page-shell">
        <div className="page-inner">
          <div style={{background:'#fff',borderRadius:'var(--r-xl)',padding:'52px 36px',textAlign:'center',border:'1px solid var(--border-mid)',boxShadow:'var(--shadow-md)'}}>
            <div style={{width:72,height:72,background:'var(--gradient)',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:32,margin:'0 auto 20px',boxShadow:'var(--shadow-green)'}}>✅</div>
            <h2 style={{fontFamily:'DM Serif Display,serif',fontSize:24,marginBottom:10}}>Message Received!</h2>
            <p style={{color:'var(--text-muted)',fontSize:15,marginBottom:24,fontWeight:300,lineHeight:1.6}}>
              Our support team will reply to <strong style={{color:'var(--primary)'}}>{currentUser?.email || form.email}</strong> within 24 hours.
            </p>
            <div className="info-box-green" style={{marginBottom:24,textAlign:'left'}}>
              <span>💡</span>
              <span>For urgent issues, include your <strong>reference number</strong> when following up.</span>
            </div>
            <button className="btn-cta" onClick={() => navigate(currentUser ? '/dashboard' : '/')}>
              {currentUser ? 'Back to Dashboard' : 'Back to Home'}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      <Navbar />
      <div className="page-shell">
        <div className="page-inner">
          <div className="page-eyebrow">💬 We're Here to Help</div>
          <h1 className="page-title">Support Center</h1>
          <p className="page-sub">Pick a topic and we'll get back to you within 24 hours</p>

          {/* Topic grid */}
          <p className="section-tag" style={{marginBottom:12}}>What do you need help with?</p>
          <div className="support-grid">
            {TOPICS.map(t => (
              <div key={t.id}
                className={`support-topic${topic===t.id?' active':''}`}
                onClick={() => setTopic(t.id)}>
                <div className="support-topic-icon">{t.icon}</div>
                <div className="support-topic-name">{t.name}</div>
                <div className="support-topic-desc">{t.desc}</div>
              </div>
            ))}
          </div>
          {errs.topic && <p className="inline-err" style={{marginBottom:14}}>⚠ Please select a topic above</p>}

          {/* Message form */}
          <div className="support-form-card">
            <div className="support-form-hdr">
              <div className="support-form-hdr-icon">✍️</div>
              <div>
                <h3>Send a Message</h3>
                <p>Describe your issue and we'll help you out</p>
              </div>
            </div>

            <div className="support-form-body">

              {/* Only show name/email if not logged in */}
              {!currentUser && (
                <>
                  <p className="section-tag">Your Details</p>
                  <input className={`loan-input${errs.name?' err':''}`}
                    placeholder="Your full name"
                    value={form.name} onChange={upd('name')} />
                  {errs.name && <p className="inline-err">⚠ Please enter your name</p>}

                  <input className={`loan-input${errs.email?' err':''}`}
                    type="email" placeholder="Your email address"
                    value={form.email} onChange={upd('email')} />
                  {errs.email && <p className="inline-err">⚠ Please enter your email</p>}
                </>
              )}

              {/* If logged in, show who we'll reply to */}
              {currentUser && (
                <div className="info-box-blue" style={{marginBottom:18}}>
                  <span>ℹ️</span>
                  <span>We will reply to <strong>{currentUser.email}</strong></span>
                </div>
              )}

              <p className="field-label">Your Message</p>
              <textarea
                className="support-textarea"
                placeholder="Describe your issue here. Include your phone number, loan reference number, or any other relevant details…"
                value={form.message}
                onChange={upd('message')}
                style={{
                  borderColor: errs.message ? 'var(--red-500)' : undefined,
                  minHeight: 160,
                }}
              />
              {errs.message && <p className="inline-err" style={{marginTop:6}}>⚠ Please write at least 10 characters</p>}

              <button className="btn-cta" style={{marginTop:18}} onClick={handleSubmit} disabled={loading}>
                {loading ? <><span className="mini-spin"></span>Sending…</> : 'Send Message →'}
              </button>
            </div>
          </div>

          <button className="btn-secondary" style={{marginTop:16}}
            onClick={() => navigate(currentUser ? '/dashboard' : '/')}>
            ← Back
          </button>
        </div>
      </div>
    </>
  );
}
